import type { ChatProvider } from '#types/plugin/chat'

declare global {
  interface Window {
    sierra: {
      clearPersistedConversation: () => void
      closeChatModal: () => void
      openChatModal: () => void
      updateVariables: (variables: { [name: string]: any }) => void
    }
    sierraConfig: {
      customGreeting?: string
      variables?: {
        consumerId?: string
        isLoyaltyMember?: boolean
      }
    }
  }
}

export default (): ChatProvider => {
  const { $t } = useNuxtApp()
  const auth = useAuthStore()
  const user = useUserStore()
  const isAvailable = ref(false)
  const { details, isLoyaltyMember } = storeToRefs(user)

  window.sierraConfig = {}

  const updateSierraWithUserData = (profileDetails) => {
    if (!profileDetails) return
    const userData = {
      variables: {
        consumerId: details.value?.consumerId,
        isLoyaltyMember: isLoyaltyMember.value
      }
    }
    Object.assign(window.sierraConfig, userData)
    if (window.sierra && userData.variables)
      window.sierra.updateVariables(userData.variables)

    const firstName = profileDetails.firstName || ''
    if (auth.loggedIn) {
      if (isLoyaltyMember.value) {
        window.sierraConfig.customGreeting = replaceAll($t.sierraChatGreetingLoggedInAndLoyalty, {
          brandName: $t.brandName,
          firstName
        })
      }
      else {
        window.sierraConfig.customGreeting = replaceAll($t.sierraChatGreetingLoggedInAndNotLoyalty, {
          firstName
        })
      }
    }
    else {
      window.sierraConfig.customGreeting = $t.sierraChatGreeting
    }
  }

  const setSierraConfig = () => {
    const sierraConfig = {
      customGreeting: $t.sierraChatGreeting,
      customStyle: { zIndex: '9999' },
      dimensions: {
        width: '25rem',
        height: '40rem'
      },
      display: 'corner'
    }
    Object.assign(window.sierraConfig, sierraConfig)
  }

  const setSierraChat = () => {
    const isCookieAccepted = hasCookieConsent(['required', 'performanceAndAnalytics', 'functional', 'targeting'])
    try {
      if (isCookieAccepted) {
        setSierraConfig()
        updateSierraWithUserData(details.value)
        loadScript('https://sierra.chat/agent/mS6nPAgBlE2009YBlE21KNq6Ntoxx_ftrix3K0nhCHY/embed', { type: 'module' })
        isAvailable.value = true
      }
    }
    catch (err) {
      console.error('Failed to load Sierra chat script:', err)
      isAvailable.value = false
    }
  }

  const closeChat = () => {
    if (window.sierra) {
      window.sierra.clearPersistedConversation()
      window.sierra.closeChatModal()
    }
  }

  watch([() => auth.loggedIn, () => details.value], () => {
    if (auth.loggedIn) {
      updateSierraWithUserData(details.value)
    }
    else {
      window.sierraConfig.customGreeting = $t.sierraChatGreeting
      delete window.sierraConfig.variables
    }
    closeChat()
    setSierraChat()
  }, { immediate: true })

  setSierraChat()

  return {
    provide: {
      chat: {
        open: (): void => {
          if (window.sierra) window.sierra.openChatModal()
        },
        available: isAvailable
      }
    }
  }
}
